import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { IBusinessFull } from "../../../interfaces/businessFull.interface";
import {
  formatEventRange,
  groupEventDays,
  IEventDayRange,
} from "../groupEventDays";
import {
  deleteMockEvent,
  listMockEvents,
  MockEventTag,
  upsertMockEvent,
} from "../qaToolsApi";

const { RangePicker } = DatePicker;

interface Props {
  business: IBusinessFull;
}

/** Inclusive local dates. POST with no days is a no-op on `/mock/events`. */
function eachIsoDay(from: Dayjs, to: Dayjs): string[] {
  const days: string[] = [];
  let cursor = from.startOf("day");
  const end = to.startOf("day");
  while (!cursor.isAfter(end, "day")) {
    days.push(cursor.format("YYYY-MM-DD"));
    cursor = cursor.add(1, "day");
  }
  return days;
}

function defaultRange(): [Dayjs, Dayjs] {
  return [dayjs().subtract(3, "day"), dayjs().subtract(1, "day")];
}

export default function BusinessEventsForm({ business }: Props) {
  const [form] = Form.useForm();
  const [range, setRange] = useState<[Dayjs, Dayjs]>(defaultRange);
  const [tags, setTags] = useState<MockEventTag[]>([]);
  const [editing, setEditing] = useState<MockEventTag>();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();

  const businessId = String(business._id);

  const refresh = async (): Promise<MockEventTag[]> => {
    setIsLoading(true);
    setError(undefined);
    try {
      const listed = await listMockEvents(businessId);
      const next = listed.tags ?? [];
      setTags(next);
      return next;
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          err?.message ??
          "Could not load business events"
      );
      return tags;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // businessId is the only input the list is keyed on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const closeForm = () => {
    setIsFormOpen(false);
    setEditing(undefined);
    form.resetFields();
    setRange(defaultRange());
    setError(undefined);
  };

  const startCreate = () => {
    setEditing(undefined);
    form.resetFields();
    setRange(defaultRange());
    setError(undefined);
    setIsFormOpen(true);
  };

  const startEdit = (row: MockEventTag) => {
    setEditing(row);
    form.setFieldsValue({ label: row.label });
    setRange(defaultRange());
    setError(undefined);
    setIsFormOpen(true);
  };

  const submit = async (values: { label: string }) => {
    const days = eachIsoDay(range[0], range[1]);
    if (!days.length) {
      setError("Pick a date range so the event has at least one day");
      return;
    }

    setIsSaving(true);
    setError(undefined);
    try {
      await upsertMockEvent({
        businessId,
        label: editing?.label ?? values.label,
        days,
      });
      const next = await refresh();
      if (editing) {
        setEditing(next.find((tag) => tag.key === editing.key));
        setRange(defaultRange());
      } else {
        closeForm();
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          err?.message ??
          "Could not save the business event"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const removeTag = async (row: MockEventTag) => {
    setError(undefined);
    try {
      await deleteMockEvent({ businessId, key: row.key });
      if (editing?.key === row.key) closeForm();
      await refresh();
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          err?.message ??
          "Could not remove the business event"
      );
    }
  };

  const removeRange = async (row: MockEventTag, span: IEventDayRange) => {
    setError(undefined);
    try {
      await deleteMockEvent({ businessId, key: row.key, days: span.days });
      const next = await refresh();
      if (editing?.key === row.key) {
        setEditing(next.find((tag) => tag.key === row.key));
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          err?.message ??
          "Could not remove that range"
      );
    }
  };

  if (isFormOpen) {
    const existingRanges = editing ? groupEventDays(editing.days) : [];

    return (
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <Typography.Title level={5} style={{ margin: 0 }}>
          {editing ? "Edit event" : "Create event"}
        </Typography.Title>

        {error && <Alert type="error" showIcon message={error} />}

        {editing && (
          <Alert
            type="info"
            showIcon
            message={`Adding days to "${editing.label}"`}
            description="The name stays put — a new label would create a second event. Remove a range below, or add another."
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={submit}
          initialValues={{ label: "" }}
        >
          <Form.Item
            label="Name"
            name="label"
            rules={[{ required: true, message: "An event name is required" }]}
          >
            <Input
              placeholder="Quiz night"
              maxLength={80}
              disabled={!!editing}
            />
          </Form.Item>
          <Form.Item label="Range" required>
            <RangePicker
              value={range}
              allowClear={false}
              onChange={(next) =>
                next?.[0] && next[1] && setRange([next[0], next[1]])
              }
            />
          </Form.Item>

          {editing && (
            <Form.Item label="Existing ranges">
              {existingRanges.length ? (
                <Space size={[4, 4]} wrap>
                  {existingRanges.map((span) => (
                    <Tag
                      key={`${span.from}-${span.to}-${span.weekdays.join(",")}`}
                      closable
                      onClose={(event) => {
                        event.preventDefault();
                        void removeRange(editing, span);
                      }}
                    >
                      {formatEventRange(span)}
                    </Tag>
                  ))}
                </Space>
              ) : (
                <Typography.Text type="secondary">None</Typography.Text>
              )}
            </Form.Item>
          )}

          <Space>
            <Button type="primary" htmlType="submit" loading={isSaving}>
              {editing ? "Add range" : "Create event"}
            </Button>
            <Button onClick={closeForm}>Back to list</Button>
          </Space>
        </Form>
      </Space>
    );
  }

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Space
        style={{ width: "100%", justifyContent: "space-between" }}
        align="start"
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Writes the same event tags the merchant forecast reads, on non-prod.
          Upcoming days show on the forecast immediately. Past days need an
          insights resync before the model can train. Three or more days makes
          the tag learnable.
        </Typography.Paragraph>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={startCreate}
          style={{ flexShrink: 0 }}
        >
          Create event
        </Button>
      </Space>

      {error && <Alert type="error" showIcon message={error} />}

      <Table
        size="small"
        rowKey="key"
        loading={isLoading}
        dataSource={tags}
        pagination={false}
        locale={{ emptyText: "No business events on this merchant yet" }}
        columns={[
          { title: "Name", dataIndex: "label", width: 180 },
          {
            title: "Ranges",
            key: "ranges",
            render: (_unused, row) => {
              const ranges = groupEventDays(row.days);
              if (!ranges.length) {
                return <Typography.Text type="secondary">None</Typography.Text>;
              }

              return (
                <Space direction="vertical" size={0}>
                  {ranges.map((span) => (
                    <Typography.Text
                      key={`${span.from}-${span.to}-${span.weekdays.join(",")}`}
                    >
                      {formatEventRange(span)}
                    </Typography.Text>
                  ))}
                </Space>
              );
            },
          },
          {
            title: "Model",
            key: "learnable",
            width: 120,
            render: (_unused, row) =>
              row.isLearnable ? (
                <Tag color="green">Learnable</Tag>
              ) : (
                <Tag>Needs {Math.max(0, 3 - row.occurrences)} more</Tag>
              ),
          },
          {
            title: "",
            key: "actions",
            width: 88,
            render: (_unused, row) => (
              <Space>
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  aria-label={`Edit ${row.label}`}
                  onClick={() => startEdit(row)}
                />
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  aria-label={`Remove ${row.label}`}
                  onClick={() => void removeTag(row)}
                />
              </Space>
            ),
          },
        ]}
      />
    </Space>
  );
}
